"use client"

import { useState, useEffect, useRef, useCallback } from "react"

const TTL_MS = 3600000 // 1 hour

/**
 * Custom hook for managing filter state with localStorage persistence and rolling expiration.
 * 
 * @param {string} storageKey - The localStorage key to use
 * @param {object} defaultFilters - Default filter values
 * @param {object} options - Optional configuration
 * @param {function} options.onLoad - Transform function called when loading filters (receives raw filters, returns transformed)
 * @param {function} options.onSave - Transform function called when saving filters (receives filters, returns transformed for storage)
 * @returns {[object, function, function]} - [filters, setFilters, resetFilters]
 */
export function useFilterStorage(storageKey, defaultFilters, options = {}) {
  const { onLoad, onSave } = options
  const isInitialMount = useRef(true)
  const defaultFiltersRef = useRef(defaultFilters)

  // Load filters from localStorage
  const loadFilters = useCallback(() => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const parsed = JSON.parse(saved)
        
        // Check if filters have expiration timestamp
        if (parsed && typeof parsed === 'object' && 'savedAt' in parsed) {
          const now = Date.now()
          const savedAt = parsed.savedAt
          
          // Check if expired (older than 1 hour)
          if (now - savedAt > TTL_MS) {
            // Expired - remove from storage and return defaults
            localStorage.removeItem(storageKey)
            return defaultFiltersRef.current
          }
          
          // Not expired - update timestamp to reset expiration timer (rolling expiration)
          const filters = parsed.filters || parsed
          const updatedData = {
            filters: filters,
            savedAt: now, // Reset timestamp to current time
          }
          localStorage.setItem(storageKey, JSON.stringify(updatedData))
          
          // Merge with defaults and apply transform if provided
          const mergedFilters = { ...defaultFiltersRef.current, ...filters }
          return onLoad ? onLoad(mergedFilters) : mergedFilters
        }
        
        // Legacy format (no timestamp) - treat as expired and remove
        localStorage.removeItem(storageKey)
      }
    } catch (error) {
      console.error('Error loading filters:', error)
    }
    
    return defaultFiltersRef.current
  }, [storageKey, onLoad])

  const [filters, setFilters] = useState(loadFilters)

  // Save filters to localStorage when they change
  useEffect(() => {
    // Skip initial mount to avoid unnecessary save
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    try {
      // Apply transform if provided
      const filtersToSave = onSave ? onSave(filters) : filters
      
      const dataToStore = {
        filters: filtersToSave,
        savedAt: Date.now(),
      }
      localStorage.setItem(storageKey, JSON.stringify(dataToStore))
    } catch (error) {
      console.error('Error saving filters:', error)
    }
  }, [filters, storageKey, onSave])

  // Reset filters to defaults and clear storage
  const resetFilters = useCallback(() => {
    try {
      localStorage.removeItem(storageKey)
    } catch (error) {
      console.error('Error clearing saved filters:', error)
    }
    setFilters(defaultFiltersRef.current)
  }, [storageKey])

  // Update a single filter value
  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }, [])

  // Update multiple filter values at once
  const updateFilters = useCallback((updates) => {
    setFilters(prev => ({ ...prev, ...updates }))
  }, [])

  return {
    filters,
    setFilters,
    updateFilter,
    updateFilters,
    resetFilters,
  }
}
